import { useCallback, useEffect, useState } from 'react';
import { IOffer, IOfferAction } from '@models/winnerSelection/ITableDemand';

export type OfferModalKey =
    'rate' | 'ratePko' | 'techAnalysis' | 'revaluate' | 'customForm' | 'approveToStageTwo' | 'attachmentsArchive';

interface IUseOfferModals {
    /** Текущий выбранный оффер (общий для всех модалок). */
    offerToEdit: IOffer | undefined;
    /** Контекст action из header-menu — нужен для CustomForm modal. */
    currentAction: IOfferAction | undefined;
    /** Текущая открытая модалка ('rate' / 'ratePko' / …) или undefined. */
    activeModal: OfferModalKey | undefined;
    /** Открыть модалку для оффера + action. */
    open: (modal: OfferModalKey, offer: IOffer, action?: IOfferAction) => void;
    /** Закрыть текущую модалку — сбрасывает offer + action. */
    close: () => void;
}

/**
 * Управляет состоянием 6 модалок Demand-таблицы: rate / ratePko / techAnalysis /
 * revaluate / customForm / approveToStageTwo. Раньше — 6 boolean'ов + общий
 * offerToEdit + currentAction в самом компоненте.
 *
 * resetOnKey — если передан и поменялся, все модалки автоматически закрываются.
 * Используется в TableDemand для сброса при смене позиции (docId), чтобы
 * модалка от прошлой позиции не «залипала».
 */
export function useOfferModals(resetOnKey?: string): IUseOfferModals {
    const [offerToEdit, setOfferToEdit] = useState<IOffer>();
    const [currentAction, setCurrentAction] = useState<IOfferAction>();
    const [activeModal, setActiveModal] = useState<OfferModalKey>();

    const open = useCallback((modal: OfferModalKey, offer: IOffer, action?: IOfferAction) => {
        setOfferToEdit(offer);
        setCurrentAction(action);
        setActiveModal(modal);
    }, []);

    const close = useCallback(() => {
        setOfferToEdit(undefined);
        setCurrentAction(undefined);
        setActiveModal(undefined);
    }, []);

    useEffect(() => {
        if (resetOnKey === undefined) return;
        close();
    }, [resetOnKey, close]);

    return { offerToEdit, currentAction, activeModal, open, close };
}
